export interface IMcsaRadarDim {
    key: string;
    v: number;
}

export interface IMcsaRadarGeometry {
    cx: number;
    cy: number;
    rings: { points: string }[];
    spokes: { x: string; y: string }[];
    avgPoints: string;
    youPoints: string;
    dots: { x: string; y: string }[];
    labels: { text: string; left: string; top: string; tx: string }[];
}

const CX = 170;
const CY = 150;
const RADIUS = 100;
const VIEW_X = -30;
const VIEW_Y = -10;
const VIEW_W = 400;
const VIEW_H = 320;

const angleFor = (index: number, count: number) => (-Math.PI / 2) + index * (2 * Math.PI / count);

const pointFor = (index: number, count: number, radius: number) => {
    const angle = angleFor(index, count);
    return `${(CX + Math.cos(angle) * radius).toFixed(1)},${(CY + Math.sin(angle) * radius).toFixed(1)}`;
};

export const buildMcsaRadar = (dims: IMcsaRadarDim[], animate: boolean): IMcsaRadarGeometry => {
    const count = dims.length;
    const rings = [0.25, 0.5, 0.75, 1].map(factor => ({
        points: dims.map((_, index) => pointFor(index, count, RADIUS * factor)).join(' '),
    }));
    const spokes = dims.map((_, index) => {
        const [x, y] = pointFor(index, count, RADIUS).split(',');
        return { x, y };
    });
    const avgPoints = dims.map((_, index) => pointFor(index, count, RADIUS * 0.55)).join(' ');
    const valueAt = (index: number) => (animate ? dims[index].v : 0.001);
    const youPoints = dims.map((_, index) => pointFor(index, count, RADIUS * valueAt(index))).join(' ');
    const dots = dims.map((_, index) => {
        const [x, y] = pointFor(index, count, RADIUS * valueAt(index)).split(',');
        return { x, y };
    });
    const labels = dims.map((dim, index) => {
        const angle = angleFor(index, count);
        const lx = CX + Math.cos(angle) * (RADIUS + 22);
        const ly = CY + Math.sin(angle) * (RADIUS + 22);
        const cosine = Math.cos(angle);
        let tx = '-50%';
        if (cosine > 0.3) tx = '-8%';
        else if (cosine < -0.3) tx = '-92%';
        return {
            text: dim.key,
            left: `${((lx - VIEW_X) / VIEW_W * 100).toFixed(1)}%`,
            top: `${((ly - VIEW_Y) / VIEW_H * 100).toFixed(1)}%`,
            tx,
        };
    });

    return { cx: CX, cy: CY, rings, spokes, avgPoints, youPoints, dots, labels };
};
